import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { createCard } from "../api/apis";
import { Link, useNavigate } from "react-router-dom";
import { getFromLocalStorage } from "../utils/localStorage";
import { Plus, Image as ImageIcon, Star, Tag, UploadCloud } from "lucide-react";

const CreateCard = () => {
  const [cardTitle, setCardTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rarity, setRarity] = useState("");
  const [price, setPrice] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // Authentication state management
  const token = getFromLocalStorage("token");
  const adminToken = getFromLocalStorage("adminToken");
  const user = getFromLocalStorage("user");
  const userRole = getFromLocalStorage("userRole");

  // Placeholder image - users must upload their own (blank gray placeholder)
  const [imageUrl, setImageUrl] = useState("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1664' height='2496' viewBox='0 0 1664 2496'%3E%3Crect width='1664' height='2496' fill='%23374151'/%3E%3C/svg%3E");

  // Check if user is Creator Pro
  const isCreatorPro = userRole === 'creator_pro';

  // All hooks must be called at the top level before any conditional returns
  // Redirect non-Creator Pro users
  useEffect(() => {
    if (token && user && !isCreatorPro) {
      toast.error("Access denied. Creator Pro membership required to create cards.");
      navigate("/dashboard");
      return;
    }
  }, [token, user, isCreatorPro, navigate]);

  // Add event listener for click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  // Function definitions must come after hooks but before conditional returns
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const formData = {
      cardTitle,
      description,
      imageUrl,
      rarity,
      // Backend requires category (array) and duration (number)
      category: ['Custom'],
      price: Number(price),
      duration: 10,
      creatorName: user.username || user.emailId || user,
      streamerEmailId: user.emailId || user.username || user,
    };

    try {
      const response = await createCard(formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user.emailId || user.username || user,
        },
      });

      if (response.data.status === "success") {
        const Message = response.data.message;
        toast.success(Message);
        navigate("/card-inventory");
      } else {
        toast.error(response.data.error || "Card creation failed!");
      }
    } catch (error) {
      const apiMsg = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(apiMsg || "An unexpected error occurred.");
    }
  };

  const handleImageSelect = async (url) => {
    try {
      setUploadingImage(true);

      // Load the image
      const img = new window.Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        // Create canvas with portrait dimensions (1664x2496)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas to portrait dimensions
        const targetWidth = 1664;
        const targetHeight = 2496;
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Calculate scaling to cover the canvas while maintaining aspect ratio
        const imgAspect = img.width / img.height;
        const targetAspect = targetWidth / targetHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgAspect > targetAspect) {
          // Image is wider than target - fit to height
          drawHeight = targetHeight;
          drawWidth = img.width * (targetHeight / img.height);
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          // Image is taller than target - fit to width
          drawWidth = targetWidth;
          drawHeight = img.height * (targetWidth / img.width);
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }

        // Fill background with black
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        // Draw the image centered and scaled
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert canvas to base64
        const base64Image = canvas.toDataURL('image/jpeg', 0.9);
        setImageUrl(base64Image);
        setShowDropdown(false);
        setUploadingImage(false);
      };

      img.onerror = () => {
        // Fallback to original URL if canvas conversion fails
        setImageUrl(url);
        setShowDropdown(false);
        setUploadingImage(false);
      };

      img.src = url;
    } catch (error) {
      console.error('Error processing image:', error);
      setImageUrl(url);
      setShowDropdown(false);
      setUploadingImage(false);
    }
  };

  const handleLocalImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);

      // Create an image element to load the file
      const img = new window.Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        // Create canvas with portrait dimensions (1664x2496)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas to portrait dimensions
        const targetWidth = 1664;
        const targetHeight = 2496;
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Calculate scaling to cover the canvas while maintaining aspect ratio
        const imgAspect = img.width / img.height;
        const targetAspect = targetWidth / targetHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgAspect > targetAspect) {
          // Image is wider than target - fit to height
          drawHeight = targetHeight;
          drawWidth = img.width * (targetHeight / img.height);
          offsetX = (targetWidth - drawWidth) / 2;
          offsetY = 0;
        } else {
          // Image is taller than target - fit to width
          drawWidth = targetWidth;
          drawHeight = img.height * (targetWidth / img.width);
          offsetX = 0;
          offsetY = (targetHeight - drawHeight) / 2;
        }

        // Fill background with black
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        // Draw the image centered and scaled
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // Convert canvas to base64
        const base64Image = canvas.toDataURL('image/jpeg', 0.9);
        setImageUrl(base64Image);
        setShowDropdown(false);
        toast.success('Image uploaded and resized to 1664x2496 px!');
        setUploadingImage(false);
      };

      img.onerror = () => {
        toast.error('Failed to load image');
        setUploadingImage(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  // Show loading or access denied for non-Creator Pro users
  if (token && user && !isCreatorPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-4">Creator Pro membership required to create cards.</p>
          <Link
            to="/dashboard"
            className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all font-medium"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Futuristic Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/10 via-transparent to-yellow-600/10 animate-pulse"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 px-6 py-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-6">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-4">
              Create New Card
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Design and create your custom challenge cards
            </p>
          </div>

          {/* Create Card Form */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-yellow-500/20 p-8 shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Side - Image Selection */}
              <div className="space-y-6">
                <div>
                  <label className="flex items-center space-x-2 text-lg font-semibold text-yellow-400 mb-4">
                    <ImageIcon size={20} />
                    <span>Card Image</span>
                  </label>
                  <div className="relative max-w-xs mx-auto" ref={dropdownRef}>
                    <div
                      className="w-full bg-white/5 border-2 border-dashed border-yellow-500/30 rounded-xl cursor-pointer hover:border-yellow-500/50 transition-all group overflow-hidden"
                      style={{ aspectRatio: '1664/2496' }}
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      <img
                        src={imageUrl}
                        alt="Selected"
                        className="w-full h-full object-cover rounded-xl group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <p className="text-white font-medium text-center px-4">Click to change image<br /><span className="text-xs text-yellow-300">Portrait: 1664x2496 px</span></p>
                      </div>
                    </div>

                    {showDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-black/80 backdrop-blur-xl border border-yellow-500/20 rounded-xl shadow-2xl z-20 max-h-80 overflow-y-auto">
                        {/* Upload from local button */}
                        <div className="p-4">
                          <button
                            onClick={triggerImageUpload}
                            disabled={uploadingImage}
                            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                          >
                            {uploadingImage ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            ) : (
                              <UploadCloud className="w-4 h-4" />
                            )}
                            <span>{uploadingImage ? 'Uploading...' : 'Upload from Device'}</span>
                          </button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLocalImageUpload}
                            className="hidden"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Form Fields */}
              <div className="space-y-4">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Card Title */}
                  <div>
                    <label className="flex items-center space-x-2 text-lg font-semibold text-yellow-400 mb-2">
                      <Tag size={20} />
                      <span>Card Title</span>
                    </label>
                    <input
                      type="text"
                      value={cardTitle}
                      onChange={(e) => setCardTitle(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-yellow-500/20 rounded-xl text-white placeholder-gray-400 focus:border-yellow-500/50 focus:outline-none transition-all"
                      placeholder="Enter card title..."
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="flex items-center space-x-2 text-lg font-semibold text-yellow-400 mb-2">
                      <span>Description</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-yellow-500/20 rounded-xl text-white placeholder-gray-400 focus:border-yellow-500/50 focus:outline-none transition-all resize-none"
                      rows="4"
                      placeholder="Describe your card..."
                      required
                    />
                  </div>

                  {/* Rarity and Price */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center space-x-2 text-lg font-semibold text-yellow-400 mb-2">
                        <Star size={20} />
                        <span>Rarity</span>
                      </label>
                      <select
                        value={rarity}
                        onChange={(e) => setRarity(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-yellow-500/20 rounded-xl text-white focus:border-yellow-500/50 focus:outline-none transition-all"
                        required
                      >
                        <option value="" className="bg-gray-800">Select Rarity</option>
                        <option value="Common" className="bg-gray-800">Common</option>
                        <option value="Rare" className="bg-gray-800">Rare</option>
                        <option value="Epic" className="bg-gray-800">Epic</option>
                        <option value="Legendary" className="bg-gray-800">Legendary</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center space-x-2 text-lg font-semibold text-yellow-400 mb-2">
                        <span>Price (Rs)</span>
                      </label>
                      <input
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-full px-4 py-2 bg-white/5 border border-yellow-500/20 rounded-xl text-white placeholder-gray-400 focus:border-yellow-500/50 focus:outline-none transition-all"
                        placeholder="Enter price..."
                        required
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl hover:from-yellow-500 hover:to-yellow-700 transition-all font-medium"
                    >
                      <Plus size={20} />
                      <span>Create Card</span>
                    </button>
                    <Link
                      to="/my-cards"
                      className="flex items-center justify-center px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all font-medium"
                    >
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateCard;
